import { Injectable } from "@angular/core";
import { getSupabaseClient } from "../auth/supabase.client";
import type { Database, TablesInsert, Json } from "../types/database.types";
type CertificateMetadata =
  Database["public"]["Functions"]["get_certificates_metadata"]["Returns"][0];
import { CertificateDefinition } from "./models/certificate.schema";
import { CertificateSummaryHelper } from "./helpers/summary.helper";

@Injectable({ providedIn: "root" })
export class CertificateService {
  private client = getSupabaseClient();
  private definitionCache = new Map<string, CertificateDefinition>();

  async getCertificates(type: string): Promise<CertificateMetadata[]> {
    const { data } = await this.client.rpc("get_certificates_metadata", {
      type_param: type,
    });
    return data ?? [];
  }

  async getCertificate<T>(
    type: string,
    id: string,
  ): Promise<{ data: T; definition_version: number | null } | null> {
    const { data } = await this.client
      .from("certificates")
      .select("data, definition_version")
      .eq("type", type)
      .eq("id", id)
      .single();
    if (!data) return null;
    return {
      data: data.data as T,
      definition_version: data.definition_version,
    };
  }

  /**
   * Fetch a single section payload from the certificate data using an RPC that
   * returns only the selected JSON key (server-side selection).
   * Expects a server function: get_certificate_section(type_param text, id_param uuid, section_key text)
   * returning JSON object for that section or null.
   */
  async getCertificateSection<T>(
    type: string,
    id: string,
    sectionKey: string,
  ): Promise<T | null> {
    // First try the RPC (preferred: server-side JSON key selection)
    const { data, error } = await this.client.rpc("get_certificate_section", {
      type_param: type,
      id_param: id,
      section_key: sectionKey,
    });
    console.log(`getCertificateSection RPC for ${type}/${id}/${sectionKey}:`, {
      data,
      error,
    });
    if (error) {
      console.warn("getCertificateSection error", error);
      return null;
    }
    return (data as T) ?? null;
  }

  async saveCertificate(
    type: string,
    data: Json,
    definitionVersion: number,
    id?: string,
  ) {
    const session = (await this.client.auth.getSession()).data.session;
    const userId = session?.user.id;
    if (!userId) throw new Error("Not authenticated");

    // Get the certificate definition to compute summary
    const definition = await this.getCertificateDefinition(
      type,
      definitionVersion,
    );
    if (!definition) {
      throw new Error(
        `Certificate definition not found for type: ${type}, version: ${definitionVersion}`,
      );
    }

    // Compute summary lines
    const summaryLines = CertificateSummaryHelper.getSummaryLines(
      definition,
      data as Record<string, unknown>,
    );

    // Treat the route placeholder "new" as no id, allowing Supabase to generate UUID
    const normalizedId = id === "new" ? undefined : id;
    const payload: TablesInsert<"certificates"> = {
      type,
      data,
      definition_version: definitionVersion,
      summary: summaryLines,
      user_id: userId,
    };
    if (normalizedId) payload.id = normalizedId;
    const { data: res } = await this.client
      .from("certificates")
      .upsert(payload)
      .select("id")
      .single();
    return (res as { id: string } | null)?.id;
  }

  /**
   * Returns the certificate definition JSON merged with a resolved pdfTemplatePath URL.
   * If version is omitted, returns the latest version for the type.
   * Results are cached to avoid redundant API calls.
   */
  async getCertificateDefinition(
    type: string,
    version?: number,
  ): Promise<CertificateDefinition | null> {
    // Create cache key
    const cacheKey = `${type}:${version ?? "latest"}`;

    // Check cache first
    if (this.definitionCache.has(cacheKey)) {
      return this.definitionCache.get(cacheKey)!;
    }

    let query = this.client
      .from("certificate_definitions")
      .select("type, version, definition, pdf_storage_path")
      .eq("type", type)
      .order("version", { ascending: false });

    if (typeof version === "number") {
      query = query.eq("version", version);
    }

    const { data, error } = await query.limit(1).maybeSingle();
    if (error || !data) return null;

    const pdfPath = data.pdf_storage_path; // e.g. templates/XYZ.pdf
    const [bucket, ...rest] = pdfPath.split("/");
    const objectPath = rest.join("/");

    const { data: publicData } = this.client.storage
      .from(bucket)
      .getPublicUrl(objectPath);
    let pdfTemplatePath = publicData.publicUrl;

    if (!pdfTemplatePath) {
      const { data: signed } = await this.client.storage
        .from(bucket)
        .createSignedUrl(objectPath, 3600);
      pdfTemplatePath = signed?.signedUrl ?? "";
    }

    const def = data.definition as unknown as CertificateDefinition;
    const result = { ...def, pdfTemplatePath };

    // Cache the result
    this.definitionCache.set(cacheKey, result);

    return result;
  }

  /**
   * Deletes a single certificate by ID
   */
  async deleteCertificate(id: string): Promise<void> {
    const { error } = await this.client
      .from("certificates")
      .delete()
      .eq("id", id);

    if (error) {
      throw new Error(`Failed to delete certificate: ${error.message}`);
    }
  }

  /**
   * Deletes multiple certificates by their IDs
   */
  async deleteCertificates(ids: string[]): Promise<void> {
    if (ids.length === 0) return;

    const { error } = await this.client
      .from("certificates")
      .delete()
      .in("id", ids);

    if (error) {
      throw new Error(`Failed to delete certificates: ${error.message}`);
    }
  }

  /**
   * Clears the certificate definition cache.
   * Useful when definitions are updated and cache needs to be invalidated.
   */
  clearDefinitionCache(): void {
    this.definitionCache.clear();
  }
}
